import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Project, KeyGoal } from '../types';

import { useParams } from 'react-router-dom';
import { ProjectForm } from '../components/projects/ProjectForm';
import { AddProjectCard } from '../components/projects/AddProjectCard';
import { toast } from 'react-toastify';

import { HelpButton } from '../components/common/HelpButton';
import { useCustomer } from '../context/CustomerContext';
import { getAllProjects, fetchPresidioAreas, getKeyGoalsForCustomer, createProject, updateProject, deleteProject } from '../services/project-service';
import { formatMonthYear } from '../utils/date-formatter';

interface ProjectsProps {
    shouldOpenNewProject?: boolean;
    onFormOpened?: () => void;
    onShowHelp?: () => void;
}

type SortField = 'projectName' | 'overview' | 'startDate' | 'endDate' | 'presidioControlled' | 'modernizationPlanProject';
type SortDirection = 'default' | 'asc' | 'desc';

export const Projects: React.FC<ProjectsProps> = ({ shouldOpenNewProject, onFormOpened, onShowHelp }) => {
    const { customerData } = useCustomer();
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { customerId } = useParams<{ customerId?: string }>();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [currentProject, setCurrentProject] = useState<Project | null>(null);
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('default');
    const [uniqueKeyGoals, setUniqueKeyGoals] = useState<KeyGoal[]>([]);
    const [presidioAreas, setPresidioAreas] = useState<string[]>([]);

    const goalsData = customerData?.goals;

    // fetch all projects
    const fetchAllProjects = useCallback(async () => {
        setIsLoading(true);
        const data = await getAllProjects(customerId as string)
        if (data && data.projects) {
            const mappedProjects: Project[] = data.projects.map((project: any) => ({
                projectId: project.projectId,
                projectName: project.projectName || '',
                overview: project.overview || '',
                presidioControlled: project.presidioControlled || 'No',
                modernizationPlanProject: project.modernizationPlanProject || 'No',
                startDate: project.startDate || '',
                endDate: project.endDate,
                dependsOnProjectId: project.dependsOnProjectId,
                goals: project.goals || [],
                presidioAreas: project.presidioAreas || []
            }));
            setProjects(mappedProjects);
        }
        else {
            setProjects([]);
        }
        setIsLoading(false);
    }, [customerId]);

    // fetch all presidio area
    const fetchPresidioArea = async () => {
        const data: any = await fetchPresidioAreas()
        if (data && data.presidio_areas && Array.isArray(data.presidio_areas)) {
            setPresidioAreas(data.presidio_areas)
        } else {
            setPresidioAreas([])
        }
    }

    const handleAddProject = async () => {
        try {
            // Make the API call
            const data = await getKeyGoalsForCustomer(customerId as string)
            if (data && data.key_goals) {
                setUniqueKeyGoals(data.key_goals);
            }
            else {
                console.error('Failed to fetch goals:', data.status);
            }
        } catch (error) {
            console.error('Error fetching goals:', error);
        }

        // Continue with opening the form
        setCurrentProject(null);
        setIsFormOpen(true);
    };

    const getUniqueKeyGoals = useCallback(() => {
        if (!goalsData?.key_goals) return [];

        const uniqueGoals = new Map<string, KeyGoal>();
        goalsData.key_goals.forEach(goal => {
            if (!uniqueGoals.has(goal.goal)) {
                uniqueGoals.set(goal.goal, goal);
            }
        });
        return Array.from(uniqueGoals.values());
    }, [goalsData?.key_goals]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Cycle through: default -> asc -> desc -> default
            if (sortDirection === 'default') {
                setSortDirection('asc');
            } else if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else {
                setSortDirection('default');
                setSortField(null);
            }
        } else {
            // Set new field and start with ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };


    // Helper to render sort indicator
    const renderSortIndicator = (field: SortField) => {
        if (sortField !== field || sortDirection === 'default') {
            // Default state - show both arrows (unsorted)
            return (
                <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12L6 17L11 12M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        }

        return sortDirection === 'asc' ? (
            // Ascending state - show up arrow
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 6L6 1L11 6" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ) : (
            // Descending state - show down arrow
            <svg width="12" height="14" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12L6 17L11 12" stroke="#062E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    };

    // Sort projects based on current sort field and direction
    const sortedProjects = useMemo(() => {
        // If no sorting is applied (default state), return original order
        if (!sortField || sortDirection === 'default') {
            return [...projects];
        }

        return [...projects].sort((a, b) => {
            // Special handling for date fields - compare as strings (YYYY-MM format sorts correctly)
            if (sortField === 'startDate' || sortField === 'endDate') {
                const dateAValue = a[sortField] || '';
                const dateBValue = b[sortField] || '';

                if (sortDirection === 'asc') {
                    return dateAValue.toString().localeCompare(dateBValue.toString());
                } else {
                    return dateBValue.toString().localeCompare(dateAValue.toString());
                }
            }

            // Handle undefined values
            const aValue = a[sortField] || '';
            const bValue = b[sortField] || '';

            // Compare based on direction
            if (sortDirection === 'asc') {
                return aValue.toString().localeCompare(bValue.toString());
            } else {
                return bValue.toString().localeCompare(aValue.toString());
            }
        });
    }, [projects, sortField, sortDirection]);

    // Load projects from API using customer_id from URL
    useEffect(() => {
        fetchAllProjects();
    }, [customerId, fetchAllProjects]);

    // Fetch Presidio areas from API and store in localStorage
    useEffect(() => {
        fetchPresidioArea();
    }, [getUniqueKeyGoals]); // Include getUniqueKeyGoals in dependencies

    // Open new project form when triggered from sidebar
    useEffect(() => {
        if (shouldOpenNewProject) {
            setCurrentProject(null);
            setIsFormOpen(true);
            if (onFormOpened) {
                onFormOpened();
            }
        }
    }, [shouldOpenNewProject, onFormOpened]);

    const handleEditProject = async (project: Project) => {
        try {
            // Make the API call
            const data = await getKeyGoalsForCustomer(customerId as string)
            if (data && data.key_goals) {
                setUniqueKeyGoals(data.key_goals);
            }
            else {
                console.error('Failed to fetch goals:', data.status);
            }
        } catch (error) {
            console.error('Error fetching goals:', error);
        }
        setCurrentProject(project);
        setIsFormOpen(true);
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setCurrentProject(null);
    };

    const handleFormSubmit = async (project: Omit<Project, 'id'> | Project) => {
        try {
            if ('projectId' in project && customerData?.customer_id) {
                // Find the original project to get the correct project_id from customer_details
                const originalProject = projects.find(p => p.projectId === project.projectId);

                if (!originalProject) {
                    throw new Error('Original project not found');
                }

                const projectData = {
                    projectName: project.projectName,
                    startDate: project.startDate,
                    endDate: project.endDate,
                    overview: project.overview,
                    presidioControlled: project.presidioControlled,
                    modernizationPlanProject: project.modernizationPlanProject,
                    dependsOnProjectId: project.dependsOnProjectId,
                    goalIds: project.goals ? project.goals.map(goal => goal.id) : [],
                    presidioAreas: project.presidioAreas || []
                };

                const updatedProjectResponse = await updateProject(customerId as string, project.projectId as string, projectData)
        

                // Map the API response to the Project interface
                const updatedProject: Project = {
                    projectId: updatedProjectResponse.project.projectId,
                    projectName: updatedProjectResponse.project.projectName,
                    overview: updatedProjectResponse.project.overview,
                    presidioControlled: updatedProjectResponse.project.presidioControlled,
                    modernizationPlanProject: updatedProjectResponse.project.modernizationPlanProject,
                    startDate: updatedProjectResponse.project.startDate,
                    endDate: updatedProjectResponse.project.endDate,
                    dependsOnProjectId: updatedProjectResponse.project.dependsOnProjectId,
                    goals: updatedProjectResponse.project.goals,
                    presidioAreas: updatedProjectResponse.project.presidioAreas
                };

                // Update the projects list with the mapped project
                const updatedProjects = projects.map(p => p.projectId === updatedProject.projectId ? updatedProject : p);
                setProjects(updatedProjects);
                // Show success notification
                toast.success('Project updated successfully!');
            } else {
                // Add new project
                if (customerData?.customer_id) {
                    // If we have customer data, use the customer API endpoint
                    const customerId = customerData.customer_id;

                    // Format the project data according to the new API requirements
                    const projectData = {
                        projectName: project.projectName,
                        startDate: project.startDate,
                        endDate: project.endDate,
                        overview: project.overview,
                        presidioControlled: project.presidioControlled,
                        modernizationPlanProject: project.modernizationPlanProject,
                        dependsOnProjectId: project.dependsOnProjectId,
                        goalIds: project.goals ? project.goals.map(goal => goal.id) : [],
                        presidioAreas: project.presidioAreas || []
                    };

                    try {

                        const responseData = await createProject(customerId as string, projectData)
                        // Create a new project object with the response data
                        const newProject: Project = {
                            projectId: responseData.project.projectId,
                            projectName: responseData.project.projectName,
                            overview: responseData.project.overview,
                            presidioControlled: responseData.project.presidioControlled,
                            modernizationPlanProject: responseData.project.modernizationPlanProject,
                            startDate: responseData.project.startDate,
                            endDate: responseData.project.endDate,
                            dependsOnProjectId: responseData.project.dependsOnProjectId,
                            goals: responseData.project.goals || [],
                            presidioAreas: responseData.project.presidioAreas || []
                        };
                        // Update the projects list
                        setProjects([...projects, newProject]);

                        // Show success notification
                        toast.success('Project added successfully!');
                    } catch (apiError: unknown) {
                        const error = apiError as Error;
                        console.error('Real API call failed:', error);
                        toast.error(`API Error: ${error.message}. Project added to local state for demo purposes.`);
                    }
                }
            }
            setIsFormOpen(false);
        } catch (error) {
            toast.error('Failed to save project.');
        }
    };

    const handleDeleteProject = async (id: string) => {
        try {
            if (customerData?.customer_id) {
                const projectToDelete = projects.find(project => project.projectId === id);
                if (!projectToDelete) {
                    throw new Error('Project not found');
                }
                const is_project_deleted = await deleteProject(customerId as string, id)
                const updatedProjects = projects.filter(p => p.projectId !== is_project_deleted.projectId);
                setProjects(updatedProjects);
                toast.success('Project deleted successfully!');
            }
        } catch (error) {
            toast.error('Failed to delete project.');
        }
    };

    return (
        <div className="px-6 py-4 bg-gray-50 min-h-full">
            <div className="flex justify-between items-center mb-6">
                <div className='flex gap-3 items-center'>
                    <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Projects</h1>
                    {onShowHelp && <HelpButton onClick={onShowHelp} />}
                </div>
                <button
                    onClick={handleAddProject}
                    className="flex items-center border border-gray-200 rounded-md px-4 py-2 text-sm font-medium text-gray-900 bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add New Project
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">

                {isLoading ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="flex justify-center items-center py-16">
                        <AddProjectCard onClick={handleAddProject} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 rounded-lg min-w-full">
                            <thead>
                                <tr className="bg-gray-100 border-b-2 border-gray-300">
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[140px] text-sm uppercase tracking-wide">
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => handleSort('projectName')}
                                        >
                                            Project Name
                                            {renderSortIndicator('projectName')}
                                        </div>
                                    </th>

                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[300px] text-sm uppercase tracking-wide">
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => handleSort('overview')}
                                        >
                                            Project Overview and Goals
                                            {renderSortIndicator('overview')}
                                        </div>
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[100px] text-sm uppercase tracking-wide">
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => handleSort('startDate')}
                                        >
                                            Start Month
                                            {renderSortIndicator('startDate')}
                                        </div>
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[100px] text-sm uppercase tracking-wide">
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => handleSort('endDate')}
                                        >
                                            End Month
                                            {renderSortIndicator('endDate')}
                                        </div>
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[120px] text-sm uppercase tracking-wide">
                                        <div
                                            className="flex justify-between items-center cursor-pointer"
                                            onClick={() => handleSort('presidioControlled')}
                                        >
                                            Presidio Controlled
                                            {renderSortIndicator('presidioControlled')}
                                        </div>
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[140px] text-sm uppercase tracking-wide">
                                        Modernization Plan
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[150px] text-sm uppercase tracking-wide">
                                        Dependency
                                    </th>
                                    <th className="text-left p-4 border-b border-gray-300 font-bold text-[#0F2A4F] min-w-[80px] text-sm uppercase tracking-wide">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedProjects.map((project, index) => (
                                    
                                    <tr
                                        key={project.projectId}
                                        className="border-b border-gray-200 text-sm hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[140px]">{project.projectName}</td>
                                        <td className="p-4 text-[#0F2A4F] min-w-[300px]">
                                            <div className="line-clamp-2 text-sm leading-relaxed">{project.overview}</div>
                                        </td>
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[100px]">
                                            {formatMonthYear(project.startDate)}
                                        </td>
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[100px]">
                                            {formatMonthYear(project.endDate)}
                                        </td>
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[120px]">{project.presidioControlled}</td>
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[140px]">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${project.modernizationPlanProject === 'Yes'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {project.modernizationPlanProject || 'No'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-[#0F2A4F] font-medium min-w-[150px]">
                                            {project.dependsOnProjectId ? (
                                                projects.find(p => p.projectId === project.dependsOnProjectId)?.projectName || '--'
                                            ) : (
                                                '--'
                                            )}
                                        </td>
                                        <td className="p-4 min-w-[80px]">
                                            <button
                                                className='p-2 rounded hover:bg-gray-100 hover:cursor-pointer transition-colors'
                                                onClick={() => handleEditProject(project)}
                                                title="Edit Project"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 3.00023H5C4.46957 3.00023 3.96086 3.21094 3.58579 3.58601C3.21071 3.96109 3 4.46979 3 5.00023V19.0002C3 19.5307 3.21071 20.0394 3.58579 20.4144C3.96086 20.7895 4.46957 21.0002 5 21.0002H19C19.5304 21.0002 20.0391 20.7895 20.4142 20.4144C20.7893 20.0394 21 19.5307 21 19.0002V12.0002M18.375 2.62523C18.7728 2.2274 19.3124 2.00391 19.875 2.00391C20.4376 2.00391 20.9772 2.2274 21.375 2.62523C21.7728 3.02305 21.9963 3.56262 21.9963 4.12523C21.9963 4.68784 21.7728 5.2274 21.375 5.62523L12.362 14.6392C12.1245 14.8765 11.8312 15.0501 11.509 15.1442L8.636 15.9842C8.54995 16.0093 8.45874 16.0108 8.37191 15.9886C8.28508 15.9663 8.20583 15.9212 8.14245 15.8578C8.07907 15.7944 8.03389 15.7151 8.01164 15.6283C7.9894 15.5415 7.9909 15.4503 8.016 15.3642L8.856 12.4912C8.95053 12.1693 9.12453 11.8763 9.362 11.6392L18.375 2.62523Z" stroke="#062E5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                            
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <ProjectForm
                    isOpen={isFormOpen}
                    onClose={handleFormClose}
                    onSubmit={handleFormSubmit}
                    onDelete={handleDeleteProject}
                    project={currentProject}
                    keyGoals={uniqueKeyGoals}
                    presidioAreas={presidioAreas}
                    projects={projects}
                />
            </div>
        </div>
    );
};
